import { Box, ClickAwayListener, IconButton, Stack, type StackProps, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import { reverse } from '/@/utils/router';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import CardSection from '../../bayer/card-section';
import IconNotification from '../../notification/icon-notification';
import ProfileIcon from '../components/profile-icon';
import { type NavbarTabKey, useTabs, useUtilitiesTabs } from '../hooks';

export const APP_SIDEBAR_WIDTH = '80px';

export const SidebarButton = ({
    key,
    tab,
    isSelected,
    ...props
}: {
    key: string;
    tab: { label: string; icon: React.ReactNode; onClick: (e: React.MouseEvent<HTMLElement>) => void };
    isSelected: boolean;
} & StackProps) => {
    return (
        <Stack
            {...props}
            key={key}
            onClick={tab.onClick}
            justifyContent='center'
            alignItems='center'
            textAlign='center'
            p={0.5}
            sx={{
                width: '100%',
                cursor: 'pointer',
                '&:hover': {
                    '& .MuiIconButton-root': {
                        backgroundColor: 'action.hover',
                    },
                },
                ...props.sx,
            }}
        >
            <IconButton
                color='primary'
                sx={{
                    ...(isSelected && {
                        backgroundColor: 'action.hover',
                    }),
                }}
            >
                {tab.icon}
            </IconButton>
            <Typography variant='caption' color='primary' fontWeight={isSelected ? 'bold' : 'normal'}>
                {tab.label}
            </Typography>
        </Stack>
    );
};

const AppSidebar = ({ selectedTab }: { selectedTab?: NavbarTabKey }) => {
    const navigate = useNavigate();
    const [utilitiesOpen, setUtilitiesOpen] = useState(false);
    const [createRecommendationModalOpen, setCreateRecommendationModalOpen] = useState(false);

    const tabs = useTabs({
        setUtilitiesOpen,
    });
    const utilitiesTabs = useUtilitiesTabs({
        setCreateRecommendationModalOpen,
    });

    const appLogo = settings.app.favicon;

    return (
        <>
            <Stack
                sx={{
                    width: APP_SIDEBAR_WIDTH,
                    position: 'fixed',
                    height: '100%',
                    background: 'white',
                    borderRight: '1px solid #10384F1F;',
                    zIndex: 10000,
                }}
                spacing={5}
                py={3}
                alignItems='center'
            >
                {/* Sidebar Logo */}
                <Box
                    sx={{ width: '40px', height: '40px', cursor: 'pointer' }}
                    component='img'
                    src={appLogo}
                    alt='logo'
                    onClick={() => {
                        navigate(reverse('NewHome'));
                    }}
                />

                {/* Sidebar Items */}
                <Stack justifyContent='center' alignItems='center'>
                    {objectEntries(tabs).map(([key, tab]) => {
                        const isSelected = selectedTab === key;
                        return (
                            <SidebarButton
                                key={key}
                                tab={tab}
                                isSelected={isSelected}
                                data-testid={`sidebar-button-${key}`}
                            />
                        );
                    })}
                </Stack>

                {/* Sidebar bottom */}
                <Stack justifyContent='center' alignItems='center' sx={{ mt: 'auto !important' }}>
                    <IconNotification />
                    <SidebarButton
                        key={'profile'}
                        tab={{
                            label: _('Perfil'),
                            icon: <ProfileIcon />,
                            onClick: () => {
                                navigate(reverse('Profile'));
                            },
                        }}
                        isSelected={false}
                    />
                </Stack>
            </Stack>

            {utilitiesOpen && (
                <ClickAwayListener onClickAway={() => setUtilitiesOpen(false)}>
                    <Stack
                        sx={{
                            width: '500px',
                            top: 0,
                            left: APP_SIDEBAR_WIDTH,
                            position: 'fixed',
                            height: '100%',
                            background: 'white',
                            borderRight: '1px solid #10384F1F;',
                            zIndex: 100,
                            overflowY: 'auto',
                        }}
                        p={4}
                        pr={3}
                        data-testid='utilities-sidebar'
                    >
                        <Stack justifyContent='center' alignItems='center' spacing={1.5} sx={{}}>
                            {objectEntries(utilitiesTabs)
                                .filter(([key, tab]) => tab.enabled)
                                .map(([key, tab]) => {
                                    return (
                                        <CardSection
                                            key={key}
                                            title={tab.label}
                                            description={tab.description}
                                            icon={tab.icon}
                                            width='100%'
                                            onClick={() => {
                                                tab.onClick();
                                                setUtilitiesOpen(false);
                                            }}
                                            data-testid={`utilities-sidebar-card-${key}`}
                                        />
                                    );
                                })}
                        </Stack>
                    </Stack>
                </ClickAwayListener>
            )}

            <CreateRecommendationModal
                open={createRecommendationModalOpen}
                setOpen={setCreateRecommendationModalOpen}
                entrySource='app-navbar'
            />
        </>
    );
};

export default AppSidebar;
